/* RemixAddPhotos.js */

import React, { useCallback, useState, Fragment, useRef, useEffect } from 'react';
import { Button, Row, Col, Input, Modal, ModalHeader, ModalBody, InputGroup } from 'reactstrap';
import Strings from '../../translations/Translations';
import PSO from '../../services/PSO';
import { useAuth } from '../../context/auth/AuthContext';
import { toast } from 'react-toastify';
import SortableList, { SortableItem } from 'react-easy-sort';
import { arrayMoveImmutable } from 'array-move';
import FileHelper from '../../helpers/FileHelper';
import TextPhoto from '../media/TextPhoto';
import axios from 'axios';
import Unsplash from '../../services/Unsplash';
import CanvasHelper from '../../helpers/CanvasHelper';
import FilesUploadAllowed from '../../enums/FilesUploadAllowed';
import { FaEdit, FaTrash } from 'react-icons/fa';
import PhotoResizer from '../../helpers/PhotoResizer';

const REACT_APP_PZ_IMAGE_SRC = process.env.REACT_APP_PZ_IMAGE_SRC;
const mediaPackCategoriesJsonSrc = process.env.PUBLIC_URL + '/mediapackcategories.json';
const REACT_APP_MAX_UPLOAD_PHOTO_WIDTH = parseInt(process.env.REACT_APP_MAX_UPLOAD_PHOTO_WIDTH);
const REACT_APP_MAX_UPLOAD_PHOTO_HEIGHT = parseInt(process.env.REACT_APP_MAX_UPLOAD_PHOTO_HEIGHT);

export default function RemixAddPhotos({ remixMedia, setRemixMedia, pickerMediaPack, pickerUnsplash, pickerUploadPhoto, pickerTextPhoto }) {
  const { toggleLoading, authenticated } = useAuth();
  const [genresMap, setGenresMap] = useState();
  const filesInput = useRef();

  const [modalMediaPack, setModalMediaPack] = useState(false);
  const toggleModalMediapack = useCallback(() => {
    setModalMediaPack(!modalMediaPack);
  }, [setModalMediaPack, modalMediaPack]);

  const [modalTextPhoto, setModalTextPhoto] = useState(false);
  const toggleModalTextPhoto = useCallback(() => {
    setModalTextPhoto(!modalTextPhoto);
  }, [setModalTextPhoto, modalTextPhoto]);

  const [modalRemoveTextPhoto, setModalRemoveTextPhoto] = useState(false);
  const toggleModalRemoveTextPhoto = useCallback(() => {
    setModalRemoveTextPhoto(!modalRemoveTextPhoto);
  }, [setModalRemoveTextPhoto, modalRemoveTextPhoto]);

  const [modalUnsplashPicker, setModalUnsplashPicker] = useState(false);
  const toggleModalUnsplashPicker = useCallback(() => {
    setModalUnsplashPicker(!modalUnsplashPicker);
  }, [setModalUnsplashPicker, modalUnsplashPicker]);

  const [unsplashRandom, setUnsplashRandom] = useState(false);
  const [unsplashPhotos, setUnsplashPhotos] = useState();
  const [unsplashCurrentPhoto, setUnsplashCurrentPhoto] = useState();
  const [unsplashQuerySearch, setUnsplashQuerySearch] = useState();

  const [currentTextPhotoToRemove, setCurrentTextPhotoToRemove] = useState(null);

  const [modalEditTextPhoto, setModalEditTextPhoto] = useState(false);
  const toggleModalEditTextPhoto = useCallback(() => {
    setModalEditTextPhoto(!modalEditTextPhoto);
  }, [setModalEditTextPhoto, modalEditTextPhoto]);
  const [editingTextPhoto, setEditingTextPhoto] = useState();

  const [currentMediaPackStyles, setCurrentMediaPackStyles] = useState();

  const processItsForMediaGrid = useCallback(async (its, type) => {
    if (its && its.length > 0) {
      const media = [];

      if (type === 'mediapack') {
        its.forEach((item) => {
          if (item.eid) {
            item.src = REACT_APP_PZ_IMAGE_SRC.replace('{id}', item.eid);
            item.type = type;
          }
          media.push(item);
        });
      } else if (type === 'file') {
        for (let i = 0; i < its.length; i++) {
          const file = its[i];
          var FileDataUri = await FileHelper.readFile(file);

          let fileType;
          if (file && file.type) {
            if (file.type.includes('image/')) {
              fileType = 'file';
              FileDataUri = await PhotoResizer({
                src: FileDataUri,
                maxWidth: REACT_APP_MAX_UPLOAD_PHOTO_WIDTH,
                maxHeight: REACT_APP_MAX_UPLOAD_PHOTO_HEIGHT
              });
            }
            else if (file.type.includes('video/')) { fileType = 'video'; }
          }

          const item = {
            file: file,
            src: FileDataUri,
            type: fileType
          };
          media.push(item);
        }
      } else if (type === 'textphoto') {
        its.forEach((item) => {
          const itemToSet = {
            src: item.renderedImage,
            type: type,
            layers: item.layers
          };
          media.push(itemToSet);
        });
      } else if (type === 'unsplash') {
        for (let i = 0; i < its.length; i++) {
          const photo = its[i];
          if (photo && photo.urls && photo.urls.regular) {
            const FileDataUri = await CanvasHelper.getBase64ImageFromURL(photo.urls.regular);
            const item = {
              src: FileDataUri,
              type: type,
              name: `${photo.id}.jpeg`
            };
            media.push(item);
          }
        }
      }

      return media;
    } else {
      return false;
    }
  }, []);

  const getMediaPack = useCallback(async (catid) => {
    if (catid) {
      toggleLoading(true);

      const mediaPackArgs = {
        un: authenticated.un,
        al: authenticated.al,
        catid: catid,
        effect: 'e1'
      }

      try {
        const response = await PSO.getMediaPack(mediaPackArgs);

        if (response.result === 'success' && response.its) {
          let media = remixMedia;
          if (!remixMedia) {
            media = [];
          }

          const newMedia = await processItsForMediaGrid(response.its, 'mediapack');
          media = media.concat(newMedia);
          setRemixMedia(media);
        } else {
          const message = (response && response.error) ? response.error : Strings('error_on_request');
          toast.error(message);
        }
      } catch (err) {
        toast.error(Strings('error_on_get_media_pack'));
      }

      toggleLoading(false);
    }
  }, [toggleLoading, authenticated, setRemixMedia, remixMedia, processItsForMediaGrid]);

  const setFiles = useCallback(async (evt) => {
    const files = evt.target.files;

    if (files && files.length > 0) {
      let filteredFiles = [];

      for (let i = 0; i < files.length; i ++) {
        const { type, name } = files[i];
        if (FilesUploadAllowed.includes(type)) {
          // Filetype is allowed
          filteredFiles.push(files[i]);
        } else {
          // Filetype is not allowed
          toast.error(`${Strings('filetype_not_allowed')}: ${name}`);
        }
      }

      let media = remixMedia;
      if (!remixMedia) {
        media = [];
      }
      const newMedia = await processItsForMediaGrid(filteredFiles, 'file');
      media = media.concat(newMedia);
      setRemixMedia(media);
    }
  }, [processItsForMediaGrid, remixMedia, setRemixMedia]);

  const setTextPhoto = useCallback(async (layers, renderedImage, index = null) => {
    if (layers && layers.length > 0 && renderedImage) {
      let media = remixMedia;
      if (!remixMedia) {
        media = [];
      }

      const textPhotos = [{
        layers,
        renderedImage
      }];

      const newMedia = await processItsForMediaGrid(textPhotos, 'textphoto');
      if (index !== null) {
        media[index] = newMedia[0];
      } else {
        media = media.concat(newMedia);
      }

      setRemixMedia([]);
      setRemixMedia(media);
    }
  }, [processItsForMediaGrid, remixMedia, setRemixMedia]);

  const onSortEnd = useCallback((oldIndex, newIndex) => {
    setRemixMedia((array) => arrayMoveImmutable(array, oldIndex, newIndex));
  }, [setRemixMedia]);

  const removeTextPhoto = useCallback((index) => {
    setCurrentTextPhotoToRemove(index);
    toggleModalRemoveTextPhoto();
  }, [setCurrentTextPhotoToRemove, toggleModalRemoveTextPhoto]);

  const removeTextPhotoConfirm = useCallback(() => {
    const index = currentTextPhotoToRemove;
    if (index !== undefined && remixMedia) {
      let media = remixMedia;
      if (media.length > 0) {
        media = media.filter((value, i) => {
          return index !== i;
        });
      }
      setRemixMedia(media);
    }
    toggleModalRemoveTextPhoto();
  }, [currentTextPhotoToRemove, remixMedia, setRemixMedia, toggleModalRemoveTextPhoto]);

  const removeMedia = useCallback((index) => {
    if (index !== undefined && remixMedia) {
      const currentMedia = remixMedia;
      let media = remixMedia;
      if (media.length > 0) {
        media = media.filter((value, i) => {
          return index !== i;
        });
      }
      setRemixMedia(media);

      const toastId = new Date().valueOf();
      toast.info(
        <Fragment>
          <p className='mr-4 d-inline-block'>
            {Strings('photo_removed')}
          </p>
          <Button
            color='secondary'
            onClick={() => {
              setRemixMedia(currentMedia);
              toast.update(toastId, { autoClose: 1 })
            }}
          >
            {Strings('undo')}
          </Button>
        </Fragment>,
        {
          toastId: toastId,
          closeOnClick: false,
          pauseOnHover: false,
          autoClose: 10000,
        }
      );
    }
  }, [remixMedia, setRemixMedia]);

  const editTextPhoto = useCallback((index) => {
    if (index !== undefined && remixMedia) {
      let media = remixMedia;
      if (media.length > 0) {
        media = media.filter((value, i) => {
          return index === i;
        });
        if (media && media.length > 0) {
          setEditingTextPhoto({ index, media: media[0] });
          toggleModalEditTextPhoto();
        }
      }
    }
  }, [remixMedia, setEditingTextPhoto, toggleModalEditTextPhoto]);

  const triggerFilesPrompt = useCallback(() => {
    filesInput.current.click();
  }, []);

  const getMediaPackCategories = useCallback(async () => {
    toggleLoading(true);
    try {
      const _t = new Date().getTime();
      const mediaPackCategoriesJson = await axios.get(`${mediaPackCategoriesJsonSrc}?_t=${_t}`);

      if (mediaPackCategoriesJson && mediaPackCategoriesJson.data && mediaPackCategoriesJson.data.its) {
        setGenresMap(mediaPackCategoriesJson.data.its);
      }
    } catch (err) {
      console.error(err);
    }
    toggleLoading(false);
  }, [toggleLoading]);

  useEffect(() => {
    getMediaPackCategories();
  }, [getMediaPackCategories]);

  const getUnsplashRandomPhotos = useCallback(async () => {
    if (!unsplashRandom) {
      toggleLoading(true, Strings('getting_unsplash_photos'));
      try {
        const photos = await Unsplash.getRandomPhotos();
        if (photos && photos.length > 0) {
          const columns = [[], [], [], [], []];
          let column = 0;
          for (let i = 0; i < photos.length; i++) {
            columns[column].push(photos[i]);
            column = (column === columns.length - 1) ? 0 : column + 1;
          }

          setUnsplashRandom(true);
          setUnsplashPhotos(columns);
        }
      } catch (err) {
        console.error(err);
        toast.error(Strings('error_getting_unsplash_photos'));
      }
      toggleLoading(false);
    }
  }, [toggleLoading, unsplashRandom, setUnsplashRandom]);

  const getUnsplashQueryPhotos = useCallback(async () => {
    setUnsplashCurrentPhoto();

    if (unsplashQuerySearch) {
      toggleLoading(true, Strings('getting_unsplash_photos'));
      try {
        const response = await Unsplash.queryPhotos(unsplashQuerySearch);
        if (response && response.results && response.results.length > 0) {
          const photos = response.results;
          const columns = [[], [], [], [], []];
          let column = 0;
          for (let i = 0; i < photos.length; i++) {
            columns[column].push(photos[i]);
            column = (column === columns.length - 1) ? 0 : column + 1;
          }
          setUnsplashRandom(false);
          setUnsplashPhotos(columns);
        }
      } catch (err) {
        console.error(err);
        toast.error(Strings('error_getting_unsplash_photos'));
      }
      toggleLoading(false);
    } else {
      getUnsplashRandomPhotos();
    }
  }, [setUnsplashCurrentPhoto, unsplashQuerySearch, toggleLoading, getUnsplashRandomPhotos]);

  const selectUnsplashPhoto = useCallback(async (photo) => {
    toggleLoading(true);
    let media;
    if (remixMedia) {
      media = remixMedia;
    } else {
      media = [];
    }
    const newMedia = await processItsForMediaGrid([photo], 'unsplash');
    if (newMedia) {
      media = media.concat(newMedia);
      setRemixMedia(media);
      setUnsplashCurrentPhoto();
      toggleModalUnsplashPicker();
    }
    toggleLoading(false);
  }, [remixMedia, setRemixMedia, processItsForMediaGrid, toggleLoading, setUnsplashCurrentPhoto, toggleModalUnsplashPicker]);

  const emptyRemixMedia = useCallback(() => { return (!remixMedia || remixMedia.length === 0) ? true : false; }, [remixMedia]);

  return (
    <Fragment>

      <Row>
        <Col md={12}>
          {pickerMediaPack && (
            <Button
              color='primary'
              className='mr-10px pb-btn-1'
              onClick={toggleModalMediapack}
            >
              {Strings('use_media_pack')}
            </Button>
          )}
          {pickerUnsplash && (
            <Button
              color='primary'
              className=''
              onClick={() => {
                toggleModalUnsplashPicker();
                getUnsplashRandomPhotos();
              }}
            >
              {Strings('use_unsplash')}
            </Button>
          )}
          {pickerUploadPhoto && (
            <Button
              color='primary'
              className='mr-10px pb-btn-1'
              onClick={triggerFilesPrompt}
            >
              {Strings('upload_photo_clip')}
            </Button>
          )}
          {pickerTextPhoto && (
            <Button
              color='primary'
              className=''
              onClick={toggleModalTextPhoto}
            >
              {Strings('create_text_photo')}
            </Button>
          )}
        </Col>
      </Row>

      <Row className='mt-3'>
        {emptyRemixMedia() && (
          <Col md={6}>
            <div className="mt-2 alert alert-secondary fade show" role="alert">
              {Strings('no_photos')}
            </div>
          </Col>
        )}
        <Col md={12}>
          <SortableList
            onSortEnd={onSortEnd}
            className='media-grid-root'
            draggedItemClassName='media-grid-dragged'
          >
            {remixMedia && remixMedia.length > 0 && remixMedia.map(({ src, type }, index) => (
              <SortableItem key={`${src}-${index}`}>
                <div className='media-grid-item'>

                  {type !== 'video' && (
                    <img src={src} alt={src} />
                  )}
                  {type === 'video' && (
                    <video
                      playsInline
                      controls
                      controlsList='nodownload noplaybackrate noremoteplayback'
                      disablePictureInPicture
                    >
                      <source src={src} />
                    </video>
                  )}
                  
                  {type !== 'textphoto' && (
                    <button
                      className='media-grid-delete-button btn-danger'
                      onClick={() => removeMedia(index)}
                    >
                      <FaTrash />
                    </button>
                  )}

                  {type === 'textphoto' && (
                    <Fragment>
                      <button
                        className='media-grid-delete-button btn-danger'
                        onClick={() => removeTextPhoto(index)}
                      >
                        <FaTrash />
                      </button>
                      <button
                        className='media-grid-edit-button btn-secondary'
                        onClick={() => editTextPhoto(index)}
                      >
                        <FaEdit />
                      </button>
                    </Fragment>
                  )}

                </div>
              </SortableItem>
            ))}
          </SortableList>
        </Col>
      </Row>

      <Row className='mt-3 d-none'>
        <Col>
          <Input
            type='file'
            name='remix_files'
            id='remix_files'
            multiple
            onChange={setFiles}
            innerRef={filesInput}
            accept='image/*, video/*'
          />
        </Col>
      </Row>

      <Modal
        isOpen={modalMediaPack}
        toggle={toggleModalMediapack}
      >
        <ModalHeader toggle={toggleModalMediapack}>
          {Strings('media_pack_modal_title')}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <Input
                type='select'
                onChange={(evt) => {
                  const style = evt.target.value;
                  if (style && genresMap[style] && genresMap[style].its) {
                    setCurrentMediaPackStyles(genresMap[style].its);
                  } else {
                    setCurrentMediaPackStyles();
                  }
                }}
              >
                <option value=''>{Strings('select_a_style')}</option>
                {genresMap && genresMap.map((value, index) => (
                  <option key={value.name} value={index}>{value.name}</option>
                ))}
              </Input>
            </Col>
          </Row>

          <Row className='mt-3 text-center'>
            <Col>
              {currentMediaPackStyles && currentMediaPackStyles.map((value, index) => (
                <Button
                  className='mr-5px mb-2 pb-outline-btn-1'
                  outline
                  color='primary'
                  key={`${value.catid}_${index}`}
                  onClick={() => {
                    toggleModalMediapack();
                    getMediaPack(value.catid);
                    setCurrentMediaPackStyles();
                  }}
                >
                  {value.name}
                </Button>
              ))}
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={modalTextPhoto}
        toggle={toggleModalTextPhoto}
        size="lg"
      >
        <ModalHeader toggle={toggleModalTextPhoto}>
          {Strings('create_text_photo_title')}
        </ModalHeader>
        <ModalBody>
          <TextPhoto
            parentSaveCallback={setTextPhoto}
            toggle={toggleModalTextPhoto}
          />
        </ModalBody>
      </Modal>

      <Modal
        isOpen={modalEditTextPhoto}
        toggle={toggleModalEditTextPhoto}
        size="lg"
      >
        <ModalHeader toggle={toggleModalEditTextPhoto}>
          {Strings('edit_text_photo')}
        </ModalHeader>
        <ModalBody>
          <TextPhoto
            parentSaveCallback={setTextPhoto}
            editing={editingTextPhoto}
            toggle={toggleModalEditTextPhoto}
          />
        </ModalBody>
      </Modal>

      <Modal
        isOpen={modalRemoveTextPhoto}
        toggle={toggleModalRemoveTextPhoto}
      >
        <ModalHeader toggle={toggleModalRemoveTextPhoto}>
          {Strings('confirm_delete')}
        </ModalHeader>
        <ModalBody>
          <p>
            {Strings('are_you_sure_to_delete_media')}
          </p>
          <div className="text-left">
            <Button
              className="btn-wide mb-2 mr-2"
              size="md"
              color="danger"
              onClick={removeTextPhotoConfirm}
            >
              {Strings('yes_delete_media')}
            </Button>
            <Button
              className="btn-wide mb-2 mr-2"
              size="md"
              color="primary"
              onClick={toggleModalRemoveTextPhoto}
            >
              {Strings('cancel')}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={modalUnsplashPicker}
        toggle={toggleModalUnsplashPicker}
        size="lg"
      >
        <ModalHeader toggle={toggleModalUnsplashPicker}>
          {Strings('unsplash_picker')}
        </ModalHeader>
        <ModalBody>

          <Row className='mb-4'>
            <Col>
              <InputGroup>
                <Input
                  placeholder={`${Strings('search_free_photos_on_unsplash')}...`}
                  onChange={(evt) => {
                    setUnsplashQuerySearch(evt.target.value);
                  }}
                />
                <Button onClick={getUnsplashQueryPhotos}>{Strings('search')}</Button>
              </InputGroup>
            </Col>
          </Row>

          <Row>
            <Col md={(unsplashCurrentPhoto) ? 9 : 12}>
              <div className='photo-grid'>
                {unsplashPhotos && unsplashPhotos.map((column, index) => (
                  <div className='photo-grid-column' key={index}>
                    {column && column.length > 0 && column.map((photo) => (
                      <div
                        key={photo.id}
                        className="d-inline-block mr-2 mb-2 cursor-pointer"
                        onClick={() => {
                          setUnsplashCurrentPhoto(photo);
                        }}
                      >
                        <img
                          src={photo.urls.regular}
                          className='img-fluid'
                          alt={photo.id}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Col>

            {unsplashCurrentPhoto && (
              <Col md={3}>
                <div>
                  <img
                    src={unsplashCurrentPhoto.urls.regular}
                    className='img-fluid'
                    alt={unsplashCurrentPhoto.id}
                  />
                  <div
                    className='media-grid-close-icon'
                    onClick={() => {
                      setUnsplashCurrentPhoto();
                    }}
                  >
                    <i className="pe-7s-close"> </i>
                  </div>
                </div>
                <div className='text-right'>
                  <Button
                    className='mt-2'
                    color='primary'
                    size='sm'
                    outline
                    onClick={() => {
                      selectUnsplashPhoto(unsplashCurrentPhoto);
                    }}
                  >
                    {Strings('use_photo')}
                  </Button>
                </div>
              </Col>
            )}
          </Row>
          
        </ModalBody>
      </Modal>

    </Fragment>
  );
}
